import { useDeleteDeviceGroup } from 'gen/client';
import { useEffect, useMemo } from 'react';

import { UIInstance } from '@ui/Types/Address';

import { isSuppressed, suppressInstallation } from './discoverySuppression';
import DeviceDiscoveryModal from './modals/DeviceDiscoveryModal';
import { hybridStrategy } from './scanning/hybridStrategy';
import { DiscoveryPhase } from './types';
import { useDeviceDiscovery } from './useDeviceDiscovery';

interface DeviceDiscoveryManagerProps {
    autoPrompt?: boolean;
    installationID?: string | null;
    isNewDevice?: boolean;
}

export default function DeviceDiscoveryManager({
    autoPrompt = true,
    installationID = null,
    isNewDevice = false,
}: Readonly<DeviceDiscoveryManagerProps>) {
    const strategy = useMemo(() => hybridStrategy, []);

    const discovery = useDeviceDiscovery({
        autoPrompt: autoPrompt && !isSuppressed(installationID),
        installationID,
        isNewDevice,
        strategy,
        suppressInstallation,
    });

    const { trigger: deleteDeviceGroup } = useDeleteDeviceGroup(discovery.state.discovered?.groupId || '', {});

    const deleteGroup = async () => {
        const id = discovery.state.discovered?.groupId;
        if (!id) {
            return;
        }
        try {
            await deleteDeviceGroup();
        } catch (error) {
            // Non-fatal: user intent is to cancel setup; backend cleanup failure shouldn't block UI
            console.error('Failed to delete provisional device group', error);
        }
    };

    // Allow other UI parts to request opening the prompt manually via a custom browser event
    useEffect(() => {
        const handler = () => {
            if (discovery.phase === DiscoveryPhase.Idle || discovery.phase === DiscoveryPhase.Cancelled) {
                discovery.showPrompt();
            }
        };
        window.addEventListener('device-discovery:prompt', handler);
        return () => {
            window.removeEventListener('device-discovery:prompt', handler);
        };
    }, [discovery]);

    // Alias hook actions to satisfy react/jsx-handler-names rule (handlers must begin with 'handle')
    const handleCancel = () => {
        void deleteGroup();
        if (installationID) {
            suppressInstallation(installationID);
        }
        discovery.cancel();
    };
    const handleClose = () => {
        if (installationID) {
            suppressInstallation(installationID);
        }
        discovery.reset();
    };
    const handleStart = (instance: UIInstance | null) => {
        discovery.startScan(instance);
    };
    const handleSubmit = ({
        description,
        location,
        name,
        visible,
    }: {
        description: string;
        location: boolean;
        name: string;
        visible: boolean;
    }) => {
        void discovery.submit({ description, location, name, visible });
    };

    // Best-effort dynamic name from raw discovery object if available
    let dynamicName: string | undefined;
    const raw = discovery.state.discovered?.raw;
    if (raw && typeof raw === 'object') {
        const rawData = raw as Record<string, unknown>;

        // Try common name fields first
        let candidate = rawData.name || rawData.device_name || rawData.hostname;

        // If we only have group_name (which is often navigator.userAgent), try to extract something useful
        if (!candidate && rawData.group_name && typeof rawData.group_name === 'string') {
            const userAgent = rawData.group_name;

            // Extract browser and OS info from user agent
            const browserRegex = /(Chrome|Firefox|Safari|Edge)\/[\d.]+/;
            const osRegex = /(Windows|Macintosh|Linux|Android|iPhone)/;

            const browserMatch = browserRegex.exec(userAgent);
            const osMatch = osRegex.exec(userAgent);

            if (browserMatch?.[1] && osMatch?.[1]) {
                const browser = browserMatch[1];
                const os = osMatch[1];
                candidate = `${os} ${browser}`;
            } else if (osMatch?.[1]) {
                candidate = `${osMatch[1]} Device`;
            } else if (browserMatch?.[1]) {
                candidate = `${browserMatch[1]} Browser`;
            }
        }

        if (typeof candidate === 'string' && candidate.trim()) {
            dynamicName = candidate.trim();
        }
    }

    return (
        <DeviceDiscoveryModal
            discoveredName={dynamicName}
            error={discovery.state.error}
            onCancel={handleCancel}
            onClose={handleClose}
            onStart={handleStart}
            onSubmit={handleSubmit}
            open
            phase={discovery.phase}
            submitting={discovery.state.submitting}
        />
    );
}
